import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, TextField, Button, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Autocomplete } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { fetchStoneIssuedReport, fetchUniqueBatches } from '../../api/stockIssue/stockIssueApi';
import { fetchEmployees } from '../../api/employee/employeeApi';
import { fetchRawStockCodes } from '../../api/rawStockCode/rawStockCodeApi';
import { fetchStocks } from '../../api/stock/stockApi';
import { fetchPurities } from '../../api/purity/purityApi';
import { fetchColors } from '../../api/color/colorApi';
import { fetchStones } from '../../api/stone/stoneApi';
import StoneIssuedReportTable from '../../components/report/StoneIssuedReportTable';
import ReportTitle from '../../components/common/ReportTitle';
import PageLoader from '../../components/common/PageLoader';

const DEFAULT_LIMIT = 200;

const initialFilters = {
  date_from: '',
  date_to: '',
  emp_code: '',
  batch_no: '',
  stock_code: '',
  stock_name: '',
  stock_type: '',
  karatage: '',
  color: '',
  stone_type: '',
  today_only: true,
  limit: DEFAULT_LIMIT
};

const formatCsvData = (data) => {
  const header = [
    'Date', 'Emp Code', 'Stock Code', 'Stock Name', 'Stock Type', 'Karatage', 'Color', 'Stone Type', 'Qty', 'Weight', 'Pure Weight', 'Weight CTS'
  ];
  const rows = data.map(row => [
    row.date,
    row.emp_code,
    row.stock_code,
    row.stock_name,
    row.stock_type,
    row.karatage,
    row.color,
    row.stone_type,
    row.qty,
    row.weight,
    row.pure_weight,
    row.weight_cts
  ].join(','));
  return [header.join(','), ...rows].join('\n');
};

const StoneIssuedReportPage = () => {
  const normalizeFilters = (input = {}) => {
    const merged = {
      ...initialFilters,
      ...input
    };

    const hasDateRange = Boolean(merged.date_from) || Boolean(merged.date_to);

    if (hasDateRange) {
      merged.today_only = input.today_only !== undefined ? input.today_only : false;
      merged.limit = input.limit !== undefined ? input.limit : DEFAULT_LIMIT;
    } else {
      merged.today_only = input.today_only !== undefined ? input.today_only : true;
      merged.limit = input.limit !== undefined ? input.limit : DEFAULT_LIMIT;
    }

    return merged;
  };

  const [filters, setFilters] = useState(initialFilters);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [pendingFilters, setPendingFilters] = useState(filters);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_qty: 0, total_weight: 0, total_weight_cts: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Master data for dropdowns
  const [employees, setEmployees] = useState([]);
  const [stockCodes, setStockCodes] = useState([]);
  const [stockTypes, setStockTypes] = useState([]);
  const [purities, setPurities] = useState([]);
  const [colors, setColors] = useState([]);
  const [stoneTypes, setStoneTypes] = useState([]);
  const [batchNumbers, setBatchNumbers] = useState([]);

  useEffect(() => {
    fetchEmployees().then(res => setEmployees(res.data)).catch(() => setEmployees([]));
    fetchRawStockCodes().then(res => setStockCodes(res.data)).catch(() => setStockCodes([]));
    fetchStocks().then(res => setStockTypes(res.data)).catch(() => setStockTypes([]));
    fetchPurities().then(res => setPurities(res.data)).catch(() => setPurities([]));
    fetchColors().then(res => setColors(res.data)).catch(() => setColors([]));
    fetchStones().then(res => setStoneTypes(res.data)).catch(() => setStoneTypes([]));
    fetchUniqueBatches().then(res => setBatchNumbers(res.data)).catch(() => setBatchNumbers([]));
  }, []);

  useEffect(() => {
    const normalized = normalizeFilters(filters);
    setLoading(true);
    fetchStoneIssuedReport(normalized)
      .then(res => {
        setRows(res.data.rows);
        setTotals(res.data.totals);
        setError(null);
      })
      .catch(err => {
        setRows([]);
        setTotals({ total_qty: 0, total_weight: 0, total_weight_cts: 0 });
        setError(err.message || 'Error fetching report');
      })
      .finally(() => setLoading(false));
  }, [filters]);

  const handleOpenFilterDialog = () => {
    setPendingFilters(filters);
    setFilterDialogOpen(true);
  };
  const handleCloseFilterDialog = () => {
    setFilterDialogOpen(false);
  };
  const handlePendingInputChange = (e) => {
    setPendingFilters({ ...pendingFilters, [e.target.name]: e.target.value });
  };
  const handleApplyFilters = () => {
    setFilters(normalizeFilters(pendingFilters));
    setFilterDialogOpen(false);
  };
  const handleClearFilters = () => {
    setFilters(initialFilters);
    setPendingFilters(initialFilters);
    setFilterDialogOpen(false);
  };
  const handleExport = () => {
    const csvData = formatCsvData(rows);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'stone_issued_report.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLoadRecent = () => {
    setFilters(initialFilters);
  };

  const handleLoadAll = () => {
    setFilters(prev => normalizeFilters({
      ...prev,
      today_only: false,
      limit: 0
    }));
  };

  return (
    <Box>
      <PageLoader loading={loading} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <ReportTitle>Stone Issued Report</ReportTitle>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FilterListIcon />}
            onClick={handleOpenFilterDialog}
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleLoadRecent}
            disabled={loading}
          >
            Load Recent
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleLoadAll}
            disabled={loading}
          >
            Load All
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={rows.length === 0}
          >
            Export CSV
          </Button>
        </Box>
      </Box>
      <Dialog open={filterDialogOpen} onClose={handleCloseFilterDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Filter Stone Issued Report
          <IconButton
            aria-label="close"
            onClick={handleCloseFilterDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
              <TextField
                select
                label="Emp Code"
                name="emp_code"
                value={pendingFilters.emp_code}
                onChange={handlePendingInputChange}
                fullWidth
              >
                <MenuItem value="">All</MenuItem>
                {employees.map((e) => (
                  <MenuItem key={e.id} value={e.emp_code}>{e.emp_code}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
              <TextField
                select
                label="Stock Code"
                name="stock_code"
                value={pendingFilters.stock_code}
                onChange={handlePendingInputChange}
                fullWidth
              >
                <MenuItem value="">All</MenuItem>
                {stockCodes.map((sc) => (
                  <MenuItem key={sc.id} value={sc.stock_code}>{sc.stock_code}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
              <TextField
                label="Stock Name"
                name="stock_name"
                value={pendingFilters.stock_name}
                onChange={handlePendingInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
              <TextField
                select
                label="Stock Type"
                name="stock_type"
                value={pendingFilters.stock_type}
                onChange={handlePendingInputChange}
                fullWidth
              >
                <MenuItem value="">All</MenuItem>
                {stockTypes.map((s) => (
                  <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
              <TextField
                select
                label="Karatage"
                name="karatage"
                value={pendingFilters.karatage}
                onChange={handlePendingInputChange}
                fullWidth
              >
                <MenuItem value="">All</MenuItem>
                {purities.map((p) => (
                  <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
              <TextField
                select
                label="Color"
                name="color"
                value={pendingFilters.color}
                onChange={handlePendingInputChange}
                fullWidth
              >
                <MenuItem value="">All</MenuItem>
                {colors.map((c) => (
                  <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
              <TextField
                select
                label="Stone Type"
                name="stone_type"
                value={pendingFilters.stone_type}
                onChange={handlePendingInputChange}
                fullWidth
              >
                <MenuItem value="">All</MenuItem>
                {stoneTypes.map((s) => (
                  <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
              <Autocomplete
                options={batchNumbers}
                getOptionLabel={(option) => option || ''}
                value={pendingFilters.batch_no ? batchNumbers.find(b => b === pendingFilters.batch_no) || pendingFilters.batch_no : null}
                onChange={(event, newValue) => {
                  setPendingFilters({ ...pendingFilters, batch_no: newValue || '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Batch No"
                    variant="outlined"
                    placeholder="Select batch no..."
                  />
                )}
                fullWidth
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
              <TextField
                label="From Date"
                type="date"
                name="date_from"
                value={pendingFilters.date_from}
                onChange={handlePendingInputChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
              <TextField
                label="To Date"
                type="date"
                name="date_to"
                value={pendingFilters.date_to}
                onChange={handlePendingInputChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearFilters} color="secondary">Clear</Button>
          <Button onClick={handleApplyFilters} color="primary" variant="contained">Apply</Button>
        </DialogActions>
      </Dialog>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
            <CardContent>
              <Typography variant="subtitle1">Total Qty</Typography>
              <Typography variant="h6" color="inherit">{totals.total_qty}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'info.light', color: 'white' }}>
            <CardContent>
              <Typography variant="subtitle1">Total Weight</Typography>
              <Typography variant="h6" color="inherit">{Number(totals.total_weight).toFixed(3)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'warning.light', color: 'white' }}>
            <CardContent>
              <Typography variant="subtitle1">Total Weight CTS</Typography>
              <Typography variant="h6" color="inherit">{Number(totals.total_weight_cts).toFixed(3)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <StoneIssuedReportTable reportData={rows} loading={loading} error={error} />
    </Box>
  );
};

export default StoneIssuedReportPage; 